import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useAxiosInstance from "../../hooks/useAxiosInstance";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { useLocation } from "react-router-dom";
import { API_RESULT, API_VERSION } from "../../enums/common.enum";
import { errorToast, handleError, successToast } from "../../utils/common";
import CustomDialogTitle from "../../components/Common/customDialogTitle";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { Backdrop, CircularProgress } from "@mui/material";

const validationSchema = Yup.object().shape({
  supplier_code: Yup.string(),
  name: Yup.string().required("Name is required"),
  address: Yup.string().required("Address is required"),
  pincode: Yup.string()
    .length(6, "Pincode must be 6 digits")
    .required("Pincode is required"),
  city: Yup.string().required("City is required"),
  credit_days: Yup.string().max(2, "Credit days must not be more than 2 digits").required("Credit days is required"),
  phone_no: Yup.string()
    .min(10, "Phone number must be at least 10 digits")
    .required("Phone number is required"),
  gst_no: Yup.string()
    .length(15, "GST number must be 15 characters")
    .required("GST number is required"),
  pan_no: Yup.string().length(10, "PAN number must be 10 characters"),
  payment_mode: Yup.string().required("Payment mode is required"),

  upi_ids: Yup.lazy((_, { parent }) =>
    parent.payment_mode === "UPI"
      ? Yup.object({
          primary: Yup.string().required("Primary UPI ID is required"),
        })
      : Yup.object({
          primary: Yup.string().notRequired(),
        })
  ),

  bank_details: Yup.lazy((_, { parent }) =>
    parent.payment_mode === "BANK"
      ? Yup.object({
          bank: Yup.string().required("Bank name is required"),
          branch: Yup.string().notRequired(),
          account_type: Yup.string(),
          account_no: Yup.string().required("Account number is required"),
          confirm_account_no: Yup.string()
            .required("Confirm account number is required")
            .oneOf([Yup.ref("account_no")], "Account numbers must match"),
          account_name: Yup.string().required("Account name is required"),
          ifsc_code: Yup.string()
            .required("IFSC code is required")
            .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter a valid IFSC code"),
        })
      : Yup.object().shape({
          bank: Yup.string().notRequired(),
          branch: Yup.string().notRequired(),
          account_type: Yup.string().notRequired(),
          account_no: Yup.string().notRequired(),
          confirm_account_no: Yup.string().notRequired(),
          account_name: Yup.string().notRequired(),
          ifsc_code: Yup.string().notRequired(),
        })
  ),
});

const initialValues = {
  supplier_code: "",
  location_id: "",
  name: "",
  address: "",
  pincode: "",
  city: "",
  credit_days: "",
  phone_no: "",
  gst_no: "",
  pan_no: "",
  payment_mode: "UPI",
  upi_ids: {
    primary: "",
  },
  bank_details: {
    bank: "",
    branch: "",
    account_name: "",
    account_no: "",
    confirm_account_no: "",
    account_type: "CURRENT",
    ifsc_code: "",
  },
};

export function DistributorForm({
  open,
  handleClose,
  addDistributorToList,
  setSelectedDistributor,
}: any) {
  const privateInstance = useAxiosInstance();
  const location = useLocation();
  const params: any = new URLSearchParams(location.search);
  const locationId = params.get("locationId");
  const [loading, setLoading] = useState(false);

  const [cameraOpen, setCameraOpen] = useState(false);
  const qrCodeRegionId = "html5qr-code-region";
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const startScanner = (setFieldValue: any) => {
    const qrRegionId = "qr-reader";
    scannerRef.current = new Html5Qrcode(qrRegionId);

    scannerRef.current
      .start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: 250,
        },
        (decodedText, decodedResult) => {
          const urlParams = new URLSearchParams(decodedText.split('?')[1]);
          const paValue = urlParams.get('pa');

          if (paValue) {
            console.log("Extracted 'pa' value:", paValue);
            setFieldValue('upi_ids.primary', paValue);
          } else {
            console.log("No 'pa' value found in the QR code.");
          }
          stopScanner();
          setCameraOpen(false);
        },
        (errorMessage) => {
          // optionally handle decode errors
        }
      )
      .catch((err) => {
        console.error("Error starting scanner:", err);
      });
  };


  const stopScanner = async () => {
    if (
      scannerRef.current &&
      scannerRef.current.getState() === Html5QrcodeScannerState.SCANNING
    ) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        console.log("Scanner stopped");
      } catch (error) {
        console.error("Failed to stop scanner:", error);
      }
    }
  };


  const getGstData = async (val: string, setFieldValue: any) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V1}/orders/pharmacy/validate-gst?gstin=${val}`
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        setFieldValue("name", result.data.response?.business_name || "");
        setFieldValue("address", result.data.response?.address || "");
        setFieldValue("pincode", result.data.response?.pincode || "");
        if (result.data.response?.pincode) {
          getPincodeData(result.data.response?.pincode, setFieldValue);
        }
      } else {
        errorToast(result?.data?.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const getPincodeData = async (val: string, setFieldValue: any) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/search/${val}`
      );
      if (result?.data?.status === API_RESULT.SUCCESS) {
        const city = result.data.response?.city || "";
        const formattedCity = city.charAt(0).toUpperCase() + city.slice(1);
        setFieldValue("city", formattedCity);
      } else {
        errorToast(result?.data?.message);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const getIfscData = async (val: string, setFieldValue: any) => {
    setLoading(true);
    try {
      const result = await privateInstance.get(
        `${API_VERSION.V3}/search/ifsc/${val}`
      );
      if (result?.data?.BANK) {
         const bank = result?.data?.BANK || "";
         setFieldValue("bank_details.bank", bank);
         setFieldValue("bank_details.branch", result.data.BRANCH || "");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoading(true);
    const { bank_details, upi_ids, ...rest } = values;
    const cleanedValues = values.payment_mode === "UPI"
      ? { ...rest, upi_ids }
      : { ...rest, bank_details };
    const valuesToSend = { ...cleanedValues, location_id: locationId };
    try {
      const result = await privateInstance.post(
        `${API_VERSION.V3}/suppliers`,
        valuesToSend
      );
      if (result.data.status === API_RESULT.SUCCESS) {
        successToast(result.data.message);
        if (result.data.message !== "Supplier is already registered.") {
          addDistributorToList({
            supplier_id: result?.data?.response?.supplier_code,
            name: result?.data?.response?.name,
            pincode: result?.data?.response?.pincode,
            gst_no: result?.data?.response?.gst_no,
            credit_days: result?.data?.response?.credit_days,
          });
          const supplierId = result?.data?.response?.supplier_id;
          setSelectedDistributor(supplierId);
        } else {
          const supplierId = result?.data?.response?.[0]?.supplier_id;
          setSelectedDistributor(supplierId);
        }
        handleClose();
      }
    } catch (error) {
      console.error("Error creating supplier:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Backdrop
        open={loading}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2, // Ensures it's above the dialog
        }}
      >
        <CircularProgress sx={{ color: "#0058ff" }} />
      </Backdrop>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            display: 'flex',
          },
        }}
        BackdropProps={{
          sx: {
            '&.MuiBackdrop-root': {
              inert: open,
            },
          },
        }}
      >
        <CustomDialogTitle id="customized-dialog-title" onClose={handleClose}>
          Add New Supplier
        </CustomDialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
            }) => (
              <Form noValidate>
                <Grid container spacing={3}>
                  <Grid mt={1.5} container item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                      width="100%"
                    >
                      <TextField
                        size="small"
                        fullWidth
                        name="gst_no"
                        label="GST Number"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.gst_no && Boolean(errors.gst_no)}
                        helperText={touched.gst_no && errors.gst_no}
                        required
                      />
                      <Button
                        sx={{ minWidth: "fit-content" }}
                        variant="outlined"
                        disabled={
                          loading || values?.gst_no?.trim()?.length !== 15
                        }
                        onClick={() => getGstData(values.gst_no, setFieldValue)}
                      >
                        Validate
                      </Button>
                    </Box>
                  </Grid>
                  <Grid mt={1.5} container item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      name="name"
                      label="Name"
                      value={values?.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12}>
                    <TextField
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      name="address"
                      label="Address"
                      value={values?.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.address && Boolean(errors.address)}
                      helperText={touched.address && errors.address}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="pincode"
                      label="Pincode"
                      value={values?.pincode}
                      onChange={(e) => {
                        const val = e.target.value;
                        handleChange(e);
                        if (val.length === 6) {
                          getPincodeData(val, setFieldValue);
                        } else {
                          setFieldValue("city", "");
                        }
                      }}
                      onBlur={handleBlur}
                      error={touched.pincode && Boolean(errors.pincode)}
                      helperText={touched.pincode && errors.pincode}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="city"
                      label="City"
                      value={values?.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.city && Boolean(errors.city)}
                      helperText={touched.city && errors.city}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="credit_days"
                      label="Credit Days"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.credit_days && Boolean(errors.credit_days)}
                      helperText={touched.credit_days && errors.credit_days}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="phone_no"
                      label="Phone Number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone_no && Boolean(errors.phone_no)}
                      helperText={touched.phone_no && errors.phone_no}
                      required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="supplier_code"
                      label="Supplier Code"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      // error={
                      //   touched.supplier_code && Boolean(errors.supplier_code)
                      // }
                      // helperText={touched.supplier_code && errors.supplier_code}
                      // required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={4}>
                    <TextField
                      size="small"
                      fullWidth
                      name="pan_no"
                      label="PAN Number"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.pan_no && Boolean(errors.pan_no)}
                      helperText={touched.pan_no && errors.pan_no}
                      // required
                    />
                  </Grid>
                  <Grid container item xs={12} sm={5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        name="payment_mode"
                        value={values.payment_mode}
                        label="Payment Mode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.payment_mode && Boolean(errors.payment_mode)
                        }
                      >
                        <MenuItem value="UPI">UPI</MenuItem>
                        <MenuItem value="BANK">Bank Transfer</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    {values?.payment_mode === "UPI" ? (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          name="upi_ids.primary"
                          label="Primary UPI ID"
                          value={values.upi_ids.primary}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.upi_ids?.primary &&
                            Boolean(errors.upi_ids?.primary)
                          }
                          helperText={
                            touched.upi_ids?.primary && errors.upi_ids?.primary
                          }
                          required
                        />
                      </>
                    ) : null}
                  </Grid>
                  <Grid container item xs={12} sm={1}>
                    {values?.payment_mode === "UPI" ? (
                      <>
                        <IconButton
                          onClick={() => {
                            setCameraOpen(true);
                            setTimeout(() => startScanner(setFieldValue), 500);
                          }}
                          sx={{
                            backgroundColor: "primary.main",
                            color: "#fff",
                            borderRadius: 1,
                            p: 1.2,
                            height: 35,
                            width: 35,
                            "&:hover": {
                              backgroundColor: "primary.dark",
                            },
                          }}
                        >
                          <QrCodeIcon />
                        </IconButton>
                      </>
                    ) : null}
                  </Grid>
                  {values?.payment_mode === "BANK" ? (
                    <Grid container item xs={12}>
                      <Grid container spacing={3}>
                        <Grid container item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            name="bank_details.account_no"
                            label="Account Number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.bank_details?.account_no &&
                              Boolean(errors.bank_details?.account_no)
                            }
                            helperText={
                              touched.bank_details?.account_no &&
                              errors.bank_details?.account_no
                            }
                            required
                          />
                        </Grid>
                        <Grid container item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            name="bank_details.confirm_account_no"
                            label="Confirm Account Number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.bank_details?.confirm_account_no &&
                              Boolean(errors.bank_details?.confirm_account_no)
                            }
                            helperText={
                              touched.bank_details?.confirm_account_no &&
                              errors.bank_details?.confirm_account_no
                            }
                            required
                          />
                        </Grid>
                        <Grid container item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            name="bank_details.ifsc_code"
                            label="IFSC Code"
                            onChange={async (e) => {
                              const value = e.target.value.trim().toUpperCase();
                              setFieldValue("bank_details.ifsc_code", value);
                              if (value.length === 11) {
                                try {
                                  const ifscData = await getIfscData(value, setFieldValue);
                                } catch (err) {
                                  console.error("Error fetching IFSC data", err);
                                }
                              }
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.bank_details?.ifsc_code &&
                              Boolean(errors.bank_details?.ifsc_code)
                            }
                            helperText={
                              touched.bank_details?.ifsc_code && errors.bank_details?.ifsc_code
                                ? errors.bank_details?.ifsc_code
                                : values.bank_details.branch
                                  ? values.bank_details.branch
                                  : ''
                            }
                            required
                          />
                        </Grid>

                        <Grid container item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            name="bank_details.bank"
                            label="Bank Name"
                            value={values?.bank_details?.bank || ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.bank_details?.bank &&
                              Boolean(errors.bank_details?.bank)
                            }
                            helperText={
                              touched.bank_details?.bank &&
                              errors.bank_details?.bank
                            }
                            required
                          />
                        </Grid>
                        <Grid container item xs={12} sm={6}>
                          <TextField
                            size="small"
                            fullWidth
                            name="bank_details.account_name"
                            label="Account Holder Name"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.bank_details?.account_name &&
                              Boolean(errors.bank_details?.account_name)
                            }
                            helperText={
                              touched.bank_details?.account_name &&
                              errors.bank_details?.account_name
                            }
                            required
                          />
                        </Grid>

                        <Grid container item xs={12} sm={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Account Type</InputLabel>
                            <Select
                              name="bank_details.account_type"
                              value={values.bank_details.account_type}
                              label="Account Type"
                              onChange={handleChange}
                              onBlur={handleBlur}
                            >
                              <MenuItem value="SAVINGS">Savings</MenuItem>
                              <MenuItem value="CURRENT">Current</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : null}

                  <Grid container item xs={12}>
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleClose}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="submit"
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      
      <Dialog
        open={cameraOpen}
        onClose={() => {
          stopScanner();
          setCameraOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Scan QR Code</DialogTitle>
        <DialogContent>
          <Box
            id="qr-reader"
            width="100%"
            height="300px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              stopScanner();
              setCameraOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
